import React from "react";
import {
  generateTestId,
  TestID,
  TestIDWrapper,
} from "../../shared/testids/testids";
import "./COREPageHeader.less";
import { MenuItemProps, PageHeader, PageHeaderProps } from "antd";
import { Merge } from "../../shared/TypeScriptHelpers";
import { COREHeading } from "../Typography/COREHeading";
import { COREDivider } from "./COREDivider";
import { BreadcrumbItem, COREBreadcrumb } from "../Navigation/COREBreadcrumb";
import { COREIcon } from "../Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useIsMobileScreen } from "../../shared/customHoooks/useBreakpoint";
import { COREButton } from "../Action/COREButton";
import { COREDropdownMenu } from "../Form/COREDropdownMenu";
import { COREMenuItem } from "../Navigation/COREMenuItem";
import { yellow100 } from "../Content/COREColour";
import { CORELoading } from "../Feedback/CORELoading";
import { COREEmpty } from "../Content/COREEmpty";
import {
  MarkHomePageButton,
  useHomePage,
} from "../../shared/buttons/MarkHomePageButton";

export type ExtraMenu =
  | {
      extra: React.ReactNode[];
      extraResponsive: {
        onClick?: () => void;
        icon?: MenuItemProps["icon"];
        children: PageHeaderProps["extra"];
        closeWhenClick?: boolean;
      }[];
    }
  | {
      extra?: never;
      extraResponsive?: never;
    };
export type COREPageHeaderProps = Merge<
  Omit<PageHeaderProps, "extra">,
  {
    testID: TestID;
    feature?: React.ReactNode;
    breadcrumbs?: BreadcrumbItem[];
  } & ExtraMenu
>;

const mappingExtraMenuWithDropdownMenu = (
  menu: Exclude<ExtraMenu["extraResponsive"], undefined>,
  testID: TestID
) =>
  menu.map(({ onClick, icon, children, closeWhenClick = false }, index) => {
    return {
      component: (
        <COREMenuItem
          testID={`${testID}-menu-item`}
          key={`${index}-default-type`}
          icon={icon}
          onClick={onClick}
        >
          {children}
        </COREMenuItem>
      ),
      closeWhenClick: closeWhenClick,
    };
  });

export const routesWithParams = [
  "/emissions-manager/initiatives/:id",
  "/emissions-manager/overview/:id",
  "/emissions-manager/offsets/:id",
];

export const COREPageHeader = React.memo(
  ({
    title,
    testID,
    extra = [],
    feature,
    extraResponsive,
    breadcrumbs,
    ...otherProps
  }: COREPageHeaderProps) => {
    const isMobileScreen = useIsMobileScreen();

    const {
      createMenuConfig,
      showSetupHomepage,
      isHomepage,
      loading,
      sync,
      errorMenu,
      errorDashboard,
    } = useHomePage();

    const titleContent = (
      <div className={"title"}>
        {showSetupHomepage && isHomepage && (
          <COREIcon
            size="md"
            color={yellow100}
            icon={icon({ name: "home", style: "solid" })}
          />
        )}
        {feature}
        <COREHeading
          level={1}
          marginBottom={false}
          testID={(testID + "-title") as TestID}
        >
          {title}
        </COREHeading>
      </div>
    );

    if (loading && !sync) return <CORELoading />;

    if (errorMenu || errorDashboard) {
      console.error(errorMenu);
      console.error(errorDashboard);
      return (
        <COREEmpty
          description="Failed to load page data"
          testID={generateTestId(
            "page",
            "page-header-core-empty-error-to-load-data"
          )}
        />
      );
    }

    const responsiveHomepage = createMenuConfig();

    const homepageIcon = <MarkHomePageButton testID={testID} />;

    if (isMobileScreen) {
      const extraInSmScreen =
        extraResponsive && extraResponsive.length > 0
          ? [
              <COREDropdownMenu
                interactionsMenu={mappingExtraMenuWithDropdownMenu(
                  [
                    ...extraResponsive,
                    ...(showSetupHomepage ? [responsiveHomepage] : []),
                  ],
                  testID
                )}
                isDisabled={false}
                testID={(testID + "-dropdown-menu") as TestID}
                key={"extra-menu"}
                overlayStyle={{ minWidth: "192px" }}
              >
                <COREButton
                  icon={
                    <COREIcon
                      icon={icon({ name: "bars", style: "regular" })}
                      key="1"
                    />
                  }
                  key={"extra-menu-button"}
                />
              </COREDropdownMenu>,
            ]
          : showSetupHomepage
          ? [homepageIcon]
          : [];
      return (
        <TestIDWrapper testID={testID}>
          <div className={"core-page-header"}>
            <PageHeader
              title={titleContent}
              breadcrumbRender={() => (
                <COREBreadcrumb
                  testID={"page-header-title"}
                  breadcrumbs={breadcrumbs}
                />
              )}
              extra={extraInSmScreen}
              {...otherProps}
            />
            <COREDivider space={"none"} />
          </div>
        </TestIDWrapper>
      );
    }

    return (
      <TestIDWrapper testID={testID}>
        <div className={"core-page-header"}>
          <PageHeader
            title={titleContent}
            extra={[...extra, ...(showSetupHomepage ? [homepageIcon] : [])]}
            {...otherProps}
          />
          <COREDivider space={"none"} />
        </div>
      </TestIDWrapper>
    );
  }
);
